import React, { useMemo, useState } from 'react';
import { ServerResponsiveTable } from '../../../components/ServerResponsiveTable/ServerResponsiveTable';
import { Panel } from '../../../components/Panel/Panel';
import { SURVEY_REFRESH_INTERVAL_MS } from '../../../hooks/useSurveys';
import { SurveyAnswerRow, SurveyLaunch } from '../../../types/survey';
import { CellSeverity, TableAction, TableColumn } from '../../../types/responsiveTable';
import { formatSurveyDay } from '../../../utils/surveyExportUtils';
import {
  getEnabledTextQuestions,
  getQuestionLabel,
  getOtherLanguageTexts,
  localeLabel,
  getRatingQuestion,
  ratingTone,
  roundLabel,
  SURVEY_RATING_MAX,
  textAccessor,
} from '../surveyUtils';

interface Props {
  endpoint: string;
  launch: SurveyLaunch;
  /** Organization languages: with more than one, the language of each response is shown and filterable (D-A15). */
  locales?: string[];
}

const NO_ACTIONS: TableAction[] = [];
const TONE_SEVERITY: Record<ReturnType<typeof ratingTone>, CellSeverity> = {
  low: 'error',
  mid: 'warning',
  high: 'success',
};

function buildColumns(launch: SurveyLaunch, withLanguage: boolean): TableColumn<SurveyAnswerRow>[] {
  const hasRating = !!getRatingQuestion(launch.questions);
  return [
    { label: 'Fecha', accessor: 'day', type: 'calendar', sortable: false, valueFromCallBack: formatSurveyDay },
    ...(withLanguage
      ? [
          {
            label: 'Idioma',
            accessor: 'locale',
            type: 'tag',
            sortable: false,
            colorFromCallBack: () => 'info',
            // Unknown language (older answers): no tag, the table shows a plain "-"
            textKeyFromCallBack: (locale: string | null | undefined) => (locale ? localeLabel(locale) : undefined),
          } satisfies TableColumn<SurveyAnswerRow>,
        ]
      : []),
    ...(hasRating
      ? [
          {
            label: 'Valoración',
            accessor: 'rating',
            type: 'number',
            sortable: false,
            valueFromCallBack: (rating: number | null) => (rating === null ? '-' : String(rating)),
            // The table passes the shown value ("-" for no rating), not the raw one
            severityCallback: (shown: number | string | null) =>
              shown === null || shown === '-' ? null : TONE_SEVERITY[ratingTone(Number(shown))],
          } satisfies TableColumn<SurveyAnswerRow>,
        ]
      : []),
    ...getEnabledTextQuestions(launch.questions).map(
      (question, index): TableColumn<SurveyAnswerRow> => ({
        label: getQuestionLabel(question, `Pregunta ${index + 1}`),
        accessor: textAccessor(question.slot),
        type: 'string',
        sortable: false,
        filterable: false,
        valueFromCallBack: (text: string | null) => text ?? '-',
      }),
    ),
  ];
}

/** One row per person. Long texts are clamped in the table; a click opens the full response. */
export function SurveyResponsesTable({ endpoint, launch, locales = [] }: Props): React.ReactElement {
  const [selected, setSelected] = useState<SurveyAnswerRow>();
  const withLanguage = locales.length > 1;
  const columns = useMemo(() => buildColumns(launch, withLanguage), [launch, withLanguage]);
  const textQuestions = getEnabledTextQuestions(launch.questions);
  const rating = getRatingQuestion(launch.questions);

  return (
    <div className='survey-responses'>
      <ServerResponsiveTable<SurveyAnswerRow>
        key={endpoint}
        endpoint={endpoint}
        columns={columns}
        options={NO_ACTIONS}
        onRowClick={(row: SurveyAnswerRow) => setSelected(row)}
        // D-A11: only an active round gets new answers
        refreshInterval={launch.status === 'active' ? SURVEY_REFRESH_INTERVAL_MS : undefined}
      />

      <Panel
        panelType='readonlyPanel'
        visible={!!selected}
        title={selected ? `Respuesta del ${formatSurveyDay(selected.day)}` : ''}
        onHide={() => setSelected(undefined)}
        className='survey-response-panel'
      >
        {selected && (
          <dl className='survey-response-detail'>
            <div>
              <dt>Ronda</dt>
              <dd>{roundLabel(selected.launchNumber)}</dd>
            </div>
            {withLanguage && (
              <div>
                <dt>Idioma de la respuesta</dt>
                <dd className={selected.locale ? undefined : 'is-empty'}>
                  {selected.locale ? localeLabel(selected.locale) : 'Sin datos'}
                </dd>
              </div>
            )}
            {rating && (
              <div>
                <dt>{getQuestionLabel(rating, 'Valoración')}</dt>
                <dd className='survey-response-rating'>
                  {selected.rating === null ? 'Sin valorar' : `${selected.rating} / ${SURVEY_RATING_MAX}`}
                </dd>
              </div>
            )}
            {textQuestions.map((question, index) => {
              const text = selected[textAccessor(question.slot)];
              return (
                <div key={question.slot}>
                  <dt>
                    {getQuestionLabel(question, `Pregunta ${index + 1}`)}
                    {getOtherLanguageTexts(question).map(({ locale, text: translation }) => (
                      <small key={locale} className='survey-question-answers-language'>
                        <span className='survey-locale-chip'>{locale}</span>
                        <span>{translation}</span>
                      </small>
                    ))}
                  </dt>
                  <dd className={text ? undefined : 'is-empty'}>{text ?? 'Sin respuesta'}</dd>
                </div>
              );
            })}
          </dl>
        )}
      </Panel>
    </div>
  );
}
